import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useKeycloak } from "@react-keycloak/web";
import { mapPathAcrossVersions } from "./versionPathMapping";
import { getRoute } from "../../api/routes/routesApi";

// TEMPORARY (LEGACY_ONLY kill switch): while everyone is forced onto the
// legacy tree, a link written in NEW vocabulary would land on legacy's 404 —
// the banner's switch, which normally translates the path, is not there to
// click. So translate on arrival instead, reusing the same mapping.
//
// Only paths that exist in the NEW tree alone are translated, so a rewrite can
// never collide with a real legacy page:
//   - `/routes...`      legacy has no such route at all.
//   - `/connections`    the new list; legacy keeps its list at /destinations
//                       and has no bare /connections route.
// `/connections/<id>` is deliberately NOT translated: both trees use it, for
// different entities, and a legacy user reloading that page would be sent to
// the wrong record. Delete this file with the flag.
function isNewOnlyPath(pathname: string): boolean {
    const [head, id] = pathname.split("/").filter(Boolean);
    if (head === "routes") return true;
    return head === "connections" && !id;
}

type Props = { children: React.ReactNode; fallback: React.ReactNode };

export default function LegacyOnlyRedirect({ children, fallback }: Props) {
    const { keycloak, initialized } = useKeycloak();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const needsRedirect = isNewOnlyPath(pathname);
    // Held back only while a translation is actually in flight, so an
    // unauthenticated visitor still reaches the tree's own login screen.
    const [redirecting, setRedirecting] = useState(needsRedirect);

    const token = keycloak?.token;
    const hasToken = !!token && typeof token === "string" && !keycloak.isTokenExpired();

    useEffect(() => {
        if (!needsRedirect) {
            setRedirecting(false);
            return;
        }
        // check-sso has not answered yet: keep waiting rather than resolve
        // against a token that is about to arrive.
        if (!initialized) return;
        if (!hasToken) {
            // Not signed in: let the tree render so its login screen shows.
            // The redirect runs again on the way back from Keycloak.
            setRedirecting(false);
            return;
        }
        let cancelled = false;
        setRedirecting(true);
        mapPathAcrossVersions(pathname, "legacy", {
            routeIdForProvider: async () => null, // never used going to legacy
            providerIdForRoute: async (routeId) => {
                try {
                    const route = await getRoute(token as string, routeId);
                    return route?.data_providers?.[0]?.id ?? null;
                } catch {
                    // A failed lookup is not "no such route": the mapping
                    // falls back to the legacy home page either way.
                    return null;
                }
            },
        }).then((destination) => {
            if (cancelled) return;
            navigate(destination, { replace: true });
            setRedirecting(false);
        });
        return () => { cancelled = true; };
    }, [pathname, needsRedirect, initialized, hasToken, token, navigate]);

    return <>{redirecting ? fallback : children}</>;
}
